/**
 * Data Cleanup Manager
 * Allows admin to identify and fix user records with incomplete profile data
 * Features: Scan, inline edit, bulk email notifications, track fixed profiles
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from '@/utils/api';
import { toast } from 'sonner';
import { 
  AlertTriangle, RefreshCw, Mail, Download, Edit2, Check, X,
  User, Phone, Loader2, Search, Filter, ChevronDown, ChevronUp,
  Send, CheckCircle, Clock, UserX, TrendingUp, PartyPopper
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { format, formatDistanceToNow } from 'date-fns';

// Use same-origin API path on production custom domains
const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

const DataCleanupManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [totalIncomplete, setTotalIncomplete] = useState(0);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', phone_number: '' });
  const [saving, setSaving] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filterType, setFilterType] = useState('all'); // 'all', 'name', 'phone', 'notified', 'not_notified'
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Recently fixed profiles
  const [recentlyFixed, setRecentlyFixed] = useState([]);
  const [recentlyFixedStats, setRecentlyFixedStats] = useState(null);
  const [showRecentlyFixed, setShowRecentlyFixed] = useState(true);

  const fetchIncompleteProfiles = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/admin/incomplete-profiles`, {
        headers: { 'X-Admin-Token': localStorage.getItem('adminToken') },
        withCredentials: true
      });
      
      setUsers(res.data.users || []);
      setStats(res.data.stats || null);
      setTotalIncomplete(res.data.total_incomplete || 0);
    } catch (err) {
      console.error('Failed to fetch incomplete profiles:', err);
      toast.error('Failed to load incomplete profiles');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRecentlyFixed = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/incomplete-profiles/recently-fixed`, {
        headers: { 'X-Admin-Token': localStorage.getItem('adminToken') },
        withCredentials: true
      });
      
      setRecentlyFixed(res.data.recently_fixed || []);
      setRecentlyFixedStats({
        count: res.data.count || 0,
        total_notified: res.data.total_notified || 0,
        conversion_rate: res.data.conversion_rate || 0,
        avg_time_to_fix_hours: res.data.avg_time_to_fix_hours
      });
    } catch (err) {
      console.error('Failed to fetch recently fixed profiles:', err);
    }
  }, []);

  useEffect(() => {
    fetchIncompleteProfiles();
    fetchRecentlyFixed();
  }, [fetchIncompleteProfiles, fetchRecentlyFixed]);

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    const filteredUsers = getFilteredUsers();
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.user_id));
    }
  };

  const startEditing = (user) => {
    setEditingUser(user.user_id);
    setEditForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone_number: user.phone_number || ''
    });
  };

  const cancelEditing = () => {
    setEditingUser(null);
    setEditForm({ first_name: '', last_name: '', phone_number: '' });
  };

  const saveProfile = async (userId) => {
    try {
      setSaving(true);
      
      const updateData = {};
      if (editForm.first_name) updateData.first_name = editForm.first_name;
      if (editForm.last_name) updateData.last_name = editForm.last_name;
      if (editForm.phone_number !== undefined) updateData.phone_number = editForm.phone_number;
      
      await axios.put(`${API_BASE}/api/admin/user/${userId}/fix-profile`, updateData, {
        headers: { 'X-Admin-Token': localStorage.getItem('adminToken') },
        withCredentials: true
      });
      
      toast.success('Profile updated successfully');
      setEditingUser(null);
      fetchIncompleteProfiles();
    } catch (err) {
      console.error('Failed to update profile:', err);
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const sendEmails = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users to email');
      return;
    }

    try {
      setSendingEmails(true);
      const res = await axios.post(`${API_BASE}/api/admin/incomplete-profiles/send-email`, {
        user_ids: selectedUsers
      }, {
        headers: { 'X-Admin-Token': localStorage.getItem('adminToken') },
        withCredentials: true
      });
      
      toast.success(`Sent ${res.data.sent_count} emails successfully${res.data.failed_count > 0 ? `, ${res.data.failed_count} failed` : ''}`);
      setSelectedUsers([]);
      fetchIncompleteProfiles();
    } catch (err) {
      console.error('Failed to send emails:', err);
      toast.error(err.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSendingEmails(false);
    }
  };

  const exportCSV = async () => {
    try {
      setExporting(true);
      const response = await axios.get(`${API_BASE}/api/admin/incomplete-profiles/export`, {
        headers: { 'X-Admin-Token': localStorage.getItem('adminToken') },
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `incomplete_profiles_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded');
    } catch (err) {
      console.error('Failed to export:', err);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const getFilteredUsers = () => {
    let filtered = users;
    
    // Apply type filter
    if (filterType === 'name') {
      filtered = filtered.filter(u => 
        u.missing_fields?.includes('First Name') || 
        u.missing_fields?.includes('Last Name')
      );
    } else if (filterType === 'phone') {
      filtered = filtered.filter(u => u.missing_fields?.includes('Phone Number'));
    } else if (filterType === 'low_quality') {
      filtered = filtered.filter(u => u.quality_issues?.length > 0);
    } else if (filterType === 'notified') {
      filtered = filtered.filter(u => u.incomplete_profile_notified);
    } else if (filterType === 'not_notified') {
      filtered = filtered.filter(u => !u.incomplete_profile_notified);
    }
    
    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(u => 
        u.email?.toLowerCase().includes(query) ||
        u.first_name?.toLowerCase().includes(query) ||
        u.last_name?.toLowerCase().includes(query) ||
        u.name?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };

  const filteredUsers = getFilteredUsers();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#df1283]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <AlertTriangle className="w-7 h-7 text-[#FF7901]" />
            Data Cleanup Manager
          </h2>
          <p className="text-[#FDF2F8]/60 mt-1">
            Find and fix user profiles with incomplete information
          </p>
        </div>
        <Button
          onClick={() => { fetchIncompleteProfiles(); fetchRecentlyFixed(); }}
          variant="outline"
          className="border-[#4A0A66] text-white hover:bg-[#4A0A66]/20"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="space-y-4">
          {/* Recently Fixed Success Banner */}
          {recentlyFixedStats && recentlyFixedStats.count > 0 && (
            <div 
              className="bg-gradient-to-r from-green-900/40 to-emerald-900/40 border border-green-500/30 rounded-xl p-4 cursor-pointer hover:border-green-500/50 transition-colors"
              onClick={() => setShowRecentlyFixed(!showRecentlyFixed)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                    <PartyPopper className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      {recentlyFixedStats.count} Users Updated Their Profile!
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                        {recentlyFixedStats.conversion_rate}% conversion
                      </span>
                    </h3>
                    <p className="text-sm text-green-300/70">
                      {recentlyFixedStats.total_notified} notified → {recentlyFixedStats.count} fixed
                      {recentlyFixedStats.avg_time_to_fix_hours && (
                        <span className="ml-2">• Avg time to fix: {recentlyFixedStats.avg_time_to_fix_hours < 24 
                          ? `${recentlyFixedStats.avg_time_to_fix_hours}h` 
                          : `${Math.round(recentlyFixedStats.avg_time_to_fix_hours / 24)}d`
                        }</span>
                      )}
                    </p>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-green-400 transition-transform ${showRecentlyFixed ? 'rotate-180' : ''}`} />
              </div>
              
              {/* Recently Fixed Users List */}
              {showRecentlyFixed && recentlyFixed.length > 0 && (
                <div className="mt-4 pt-4 border-t border-green-500/20 space-y-2">
                  {recentlyFixed.slice(0, 5).map(user => (
                    <div key={user.user_id} className="flex items-center justify-between bg-green-500/10 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white text-xs font-bold">
                          {user.picture ? (
                            <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            (user.first_name?.[0] || '?').toUpperCase()
                          )}
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">{user.first_name} {user.last_name}</p>
                          <p className="text-green-300/60 text-xs">{user.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-green-400 text-xs flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Fixed {user.profile_updated_at ? formatDistanceToNow(new Date(user.profile_updated_at), { addSuffix: true }) : 'recently'}
                        </p>
                        {user.time_to_fix_hours && (
                          <p className="text-green-300/50 text-xs">
                            {user.time_to_fix_hours < 24 
                              ? `${user.time_to_fix_hours}h after notification` 
                              : `${user.time_to_fix_days}d after notification`
                            }
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                  {recentlyFixed.length > 5 && (
                    <p className="text-center text-green-300/50 text-xs pt-2">
                      + {recentlyFixed.length - 5} more users fixed their profiles
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Missing Data Row */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#FF7901]/20 flex items-center justify-center">
                  <UserX className="w-5 h-5 text-[#FF7901]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{totalIncomplete}</p>
                  <p className="text-sm text-[#FDF2F8]/50">Total Issues</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.missing_first_name || 0}</p>
                  <p className="text-sm text-[#FDF2F8]/50">Missing First Name</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.missing_last_name || 0}</p>
                  <p className="text-sm text-[#FDF2F8]/50">Missing Last Name</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.missing_phone || 0}</p>
                  <p className="text-sm text-[#FDF2F8]/50">Missing Phone</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Low Quality Data Row */}
          {(stats.low_quality_first_name > 0 || stats.low_quality_last_name > 0 || stats.low_quality_phone > 0) && (
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-orange-500/30 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.low_quality_first_name || 0}</p>
                    <p className="text-sm text-[#FDF2F8]/50">Low Quality First Name</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-orange-500/30 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.low_quality_last_name || 0}</p>
                    <p className="text-sm text-[#FDF2F8]/50">Low Quality Last Name</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-orange-500/30 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.low_quality_phone || 0}</p>
                    <p className="text-sm text-[#FDF2F8]/50">Low Quality Phone</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions Bar */}
      <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-4">
        <div className="flex items-center justify-between gap-4">
          {/* Search & Filters */}
          <div className="flex items-center gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#FDF2F8]/40" />
              <Input
                placeholder="Search by email or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#1a0527] border-[#4A0A66] text-white"
              />
            </div>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-2 rounded-lg border transition-colors flex items-center gap-2 ${
                showFilters || filterType !== 'all'
                  ? 'border-[#df1283] bg-[#df1283]/20 text-[#df1283]'
                  : 'border-[#4A0A66] text-white hover:bg-[#4A0A66]/20'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filter
              {filterType !== 'all' && (
                <span className="bg-[#df1283] text-white text-xs px-1.5 py-0.5 rounded">1</span>
              )}
            </button>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button
              onClick={exportCSV}
              disabled={exporting}
              variant="outline"
              className="border-[#4A0A66] text-white hover:bg-[#4A0A66]/20"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export CSV
            </Button>
            
            <Button
              onClick={sendEmails}
              disabled={selectedUsers.length === 0 || sendingEmails}
              className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white"
            >
              {sendingEmails ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Email Selected ({selectedUsers.length})
            </Button>
          </div>
        </div>
        
        {/* Filter Options */}
        {showFilters && (
          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-[#4A0A66]">
            <span className="text-sm text-[#FDF2F8]/60">Filter by:</span>
            {[
              { key: 'all', label: 'All Issues' },
              { key: 'name', label: 'Missing Name' },
              { key: 'phone', label: 'Missing Phone' },
              { key: 'low_quality', label: '⚠️ Low Quality' },
              { key: 'not_notified', label: 'Not Notified' },
              { key: 'notified', label: 'Already Notified' }
            ].map(filter => (
              <button
                key={filter.key}
                onClick={() => setFilterType(filter.key)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  filterType === filter.key
                    ? 'bg-[#df1283] text-white'
                    : 'bg-[#4A0A66]/30 text-[#FDF2F8]/70 hover:bg-[#4A0A66]/50'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-[#4A0A66] flex items-center justify-between">
          <h3 className="text-white font-semibold">
            Incomplete Profiles ({filteredUsers.length})
          </h3>
          <label className="flex items-center gap-2 text-sm text-[#FDF2F8]/60 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
              onChange={handleSelectAll}
              className="w-4 h-4 rounded border-[#4A0A66] bg-[#1a0527] text-[#df1283] focus:ring-[#df1283]"
            />
            Select All
          </label>
        </div>
        
        {filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <p className="text-white font-medium">All profiles are complete!</p>
            <p className="text-[#FDF2F8]/50 text-sm">No users with missing information found.</p>
          </div>
        ) : (
          <div className="divide-y divide-[#4A0A66]/30">
            {filteredUsers.map(user => (
              <div 
                key={user.user_id} 
                className={`px-6 py-4 hover:bg-[#4A0A66]/10 transition-colors ${
                  selectedUsers.includes(user.user_id) ? 'bg-[#df1283]/10' : ''
                }`}
              >
                <div className="flex items-center gap-4">
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user.user_id)}
                    onChange={() => handleSelectUser(user.user_id)}
                    className="w-4 h-4 rounded border-[#4A0A66] bg-[#1a0527] text-[#df1283] focus:ring-[#df1283]"
                  />
                  
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#df1283] to-[#FF7901] flex items-center justify-center text-white font-bold text-sm">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      (user.first_name?.[0] || user.email?.[0] || '?').toUpperCase()
                    )}
                  </div>
                  
                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    {editingUser === user.user_id ? (
                      // Edit Mode
                      <div className="flex items-center gap-3">
                        <Input
                          placeholder="First Name"
                          value={editForm.first_name}
                          onChange={(e) => setEditForm(prev => ({ ...prev, first_name: e.target.value }))}
                          className="w-32 bg-[#1a0527] border-[#4A0A66] text-white h-8 text-sm"
                        />
                        <Input
                          placeholder="Last Name"
                          value={editForm.last_name}
                          onChange={(e) => setEditForm(prev => ({ ...prev, last_name: e.target.value }))}
                          className="w-32 bg-[#1a0527] border-[#4A0A66] text-white h-8 text-sm"
                        />
                        <Input
                          placeholder="Phone"
                          value={editForm.phone_number}
                          onChange={(e) => setEditForm(prev => ({ ...prev, phone_number: e.target.value }))}
                          className="w-36 bg-[#1a0527] border-[#4A0A66] text-white h-8 text-sm"
                        />
                        <Button
                          size="sm"
                          onClick={() => saveProfile(user.user_id)}
                          disabled={saving}
                          className="bg-green-600 hover:bg-green-700 h-8"
                        >
                          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={cancelEditing}
                          className="text-white hover:bg-[#4A0A66]/20 h-8"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      // View Mode
                      <div>
                        <p className="text-white font-medium truncate">
                          {user.first_name || user.last_name 
                            ? `${user.first_name || '—'} ${user.last_name || '—'}`
                            : user.name || '(No name)'
                          }
                        </p>
                        <p className="text-sm text-[#FDF2F8]/50 truncate">{user.email}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Missing Fields Tags */}
                  {editingUser !== user.user_id && (
                    <div className="flex flex-wrap items-center gap-1">
                      {user.missing_fields?.map(field => (
                        <span
                          key={field}
                          className={`text-xs px-2 py-1 rounded-full ${
                            field === 'Phone Number'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {field}
                        </span>
                      ))}
                      {user.quality_issues?.map((issue, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-1 rounded-full bg-orange-500/20 text-orange-400"
                          title={user.all_issues?.find(i => issue.includes(i.field))?.value || ''}
                        >
                          ⚠️ {issue}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Notified Status */}
                  {editingUser !== user.user_id && (
                    <div className="flex items-center gap-2">
                      {user.incomplete_profile_notified ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400 flex items-center gap-1">
                          <Send className="w-3 h-3" />
                          Notified
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded-full bg-[#4A0A66]/50 text-[#FDF2F8]/50 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Not notified
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* Edit Button */}
                  {editingUser !== user.user_id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => startEditing(user)}
                      className="text-[#0FFFE2] hover:bg-[#0FFFE2]/10"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                
                {/* Additional Info */}
                {editingUser !== user.user_id && (
                  <div className="mt-2 ml-14 flex items-center gap-4 text-xs text-[#FDF2F8]/40">
                    <span>Tier: {user.membership_tier || 'free'}</span>
                    <span>•</span>
                    <span>Joined: {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : 'Unknown'}</span>
                    {user.last_login && (
                      <>
                        <span>•</span>
                        <span>Last login: {format(new Date(user.last_login), 'MMM d, yyyy')}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DataCleanupManager;
